import ExcelJS from "exceljs";
import { getAssetManagersData } from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/assetManagersData";

/**
 * Export Asset Managers Dashboard Data to Excel
 * Creates tables matching the exact data structure for comparison with UI
 */
export async function exportAssetManagersToExcel() {
  try {
    const data = getAssetManagersData();
    const portfolio = data.portfolio;
    const monthlyData = data.monthlyData;
    const currentDate = new Date().toLocaleDateString("en-GB");

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "OneVR Analytics";
    workbook.created = new Date();
    workbook.modified = new Date();

    // Create all sheets
    createPortfolioInfoSheet(workbook, portfolio);
    createESGSheet(workbook, monthlyData);
    createOccupancySheet(workbook, monthlyData);
    createMarketROISheet(workbook, monthlyData);
    createTenantExperienceSheet(workbook, monthlyData);
    createFinancialSheet(workbook, monthlyData);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Asset-Managers-${portfolio.name.replace(
      /\s+/g,
      "-"
    )}-${currentDate.replace(/\//g, "-")}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename: link.download };
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    return { success: false, error: error.message };
  }
}

// Helper functions
function addDataRow(sheet, rowNum, label, value) {
  sheet.getCell(`A${rowNum}`).value = label;
  sheet.getCell(`A${rowNum}`).font = {
    bold: true,
    size: 11,
    color: { argb: "FF374151" }
  };
  sheet.getCell(`A${rowNum}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };
  sheet.getCell(`B${rowNum}`).value = value;
  sheet.getCell(`B${rowNum}`).font = { size: 11, color: { argb: "FF1F2937" } };
  sheet.getRow(rowNum).height = 20;
}

function addSectionHeader(sheet, rowNum, text) {
  sheet.mergeCells(`A${rowNum}:B${rowNum}`);
  const cell = sheet.getCell(`A${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6B7280" }
  };
  sheet.getRow(rowNum).height = 25;
}

function createTableHeader(sheet, headers, color) {
  headers.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: color || "FF3B82F6" }
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } }
    };
  });
  sheet.getRow(1).height = 30;
}

function addDataCell(sheet, rowNum, colNum, value, isNumber = false) {
  const cell = sheet.getCell(rowNum, colNum);
  cell.value = value;
  cell.font = { size: 10, color: { argb: "FF1F2937" } };
  cell.alignment = {
    vertical: "middle",
    horizontal: isNumber ? "right" : "left"
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };
  if (isNumber && typeof value === "number") {
    cell.numFmt = value % 1 === 0 ? "#,##0" : "#,##0.0";
  }
  sheet.getRow(rowNum).height = 20;
}

function addPercentCell(sheet, rowNum, colNum, value) {
  const cell = sheet.getCell(rowNum, colNum);
  cell.value = value / 100;
  cell.font = { size: 10, color: { argb: "FF1F2937" } };
  cell.alignment = { vertical: "middle", horizontal: "right" };
  cell.numFmt = "0.0%";
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };
  sheet.getRow(rowNum).height = 20;
}

function addChangeCell(sheet, rowNum, colNum, value) {
  const cell = sheet.getCell(rowNum, colNum);
  cell.value = value / 100;
  cell.numFmt = "+0.0%;-0.0%";
  cell.font = {
    size: 10,
    color: { argb: value >= 0 ? "FF10B981" : "FFEF4444" }
  };
  cell.alignment = { vertical: "middle", horizontal: "right" };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };
  sheet.getRow(rowNum).height = 20;
}

// Sheet creation functions
function createPortfolioInfoSheet(workbook, portfolio) {
  const sheet = workbook.addWorksheet("Portfolio Info", {
    properties: { tabColor: { argb: "FF3B82F6" } }
  });
  sheet.columns = [{ width: 35 }, { width: 45 }];

  sheet.mergeCells("A1:B1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "PORTFOLIO INFORMATION & BENCHMARKS";
  titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF3B82F6" }
  };
  sheet.getRow(1).height = 35;

  let row = 3;
  addDataRow(sheet, row++, "Portfolio Name", portfolio.name);
  addDataRow(sheet, row++, "Owner", portfolio.owner);
  addDataRow(sheet, row++, "Location", portfolio.location);
  addDataRow(sheet, row++, "Total Buildings", portfolio.totalBuildings);
  addDataRow(sheet, row++, "Total Area (sqm)", portfolio.totalArea);
  addDataRow(sheet, row++, "Total Value (€)", portfolio.totalValue);
  addDataRow(sheet, row++, "Total Units", portfolio.totalUnits);

  row++;
  addSectionHeader(sheet, row++, "Buildings");
  portfolio.buildings.forEach((building) => {
    row++;
    addDataRow(sheet, row++, "Building", building.name);
    addDataRow(sheet, row++, "Value (€)", building.value);
    addDataRow(sheet, row++, "Area (sqm)", building.area);
    addDataRow(sheet, row++, "Units", building.units);
  });
}

function createESGSheet(workbook, monthlyData) {
  const sheet = workbook.addWorksheet("ESG Performance", {
    properties: { tabColor: { argb: "FF10B981" } }
  });
  sheet.columns = [
    { width: 15 },
    { width: 12 },
    { width: 15 },
    { width: 12 },
    { width: 12 },
    { width: 15 },
    { width: 12 },
    { width: 12 },
    { width: 15 },
    { width: 15 },
    { width: 15 }
  ];

  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Overall",
      "Change %",
      "Target",
      "Environmental",
      "Social",
      "Governance",
      "TechHub",
      "Innovation",
      "Business Park"
    ],
    "FF10B981"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const esg = month.esg;
    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addDataCell(sheet, row, 3, esg.overallScore, true);
    addChangeCell(sheet, row, 4, esg.changePercent);
    addDataCell(sheet, row, 5, esg.targetScore, true);
    addDataCell(sheet, row, 6, esg.environmental.score, true);
    addDataCell(sheet, row, 7, esg.social.score, true);
    addDataCell(sheet, row, 8, esg.governance.score, true);
    addDataCell(sheet, row, 9, esg.regional.techHub, true);
    addDataCell(sheet, row, 10, esg.regional.innovationCenter, true);
    addDataCell(sheet, row, 11, esg.regional.businessPark, true);
  });
}

function createOccupancySheet(workbook, monthlyData) {
  const sheet = workbook.addWorksheet("Occupancy & Revenue", {
    properties: { tabColor: { argb: "FFF59E0B" } }
  });
  sheet.columns = [
    { width: 15 },
    { width: 12 },
    { width: 15 },
    { width: 12 },
    { width: 12 },
    { width: 18 },
    { width: 18 },
    { width: 15 },
    { width: 18 },
    { width: 12 }
  ];

  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Rate %",
      "Change %",
      "Target %",
      "Revenue (€)",
      "Potential (€)",
      "Collection %",
      "Collected (€)",
      "Gap %"
    ],
    "FFF59E0B"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const occ = month.occupancy;
    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addPercentCell(sheet, row, 3, occ.overallRate);
    addChangeCell(sheet, row, 4, occ.changePercent);
    addPercentCell(sheet, row, 5, occ.targetRate);
    addDataCell(sheet, row, 6, occ.totalRevenue, true);
    addDataCell(sheet, row, 7, occ.potentialRevenue, true);
    addPercentCell(sheet, row, 8, occ.collectionRate);
    addDataCell(sheet, row, 9, occ.actualCollected, true);
    addPercentCell(sheet, row, 10, occ.efficiencyGap);
  });
}

function createMarketROISheet(workbook, monthlyData) {
  const sheet = workbook.addWorksheet("Market & ROI", {
    properties: { tabColor: { argb: "FF06B6D4" } }
  });
  sheet.columns = [
    { width: 15 },
    { width: 12 },
    { width: 18 },
    { width: 15 },
    { width: 12 },
    { width: 12 },
    { width: 15 },
    { width: 12 },
    { width: 18 },
    { width: 15 },
    { width: 12 },
    { width: 15 },
    { width: 15 }
  ];

  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Value (€)",
      "Appreciation %",
      "ROI %",
      "Cap Rate %",
      "Cash/Cash %",
      "IRR %",
      "Annual Rev",
      "NOI (€)",
      "NOI %",
      "Market %",
      "Outperf %"
    ],
    "FF06B6D4"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const roi = month.marketRoi;
    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addDataCell(sheet, row, 3, roi.portfolioValue, true);
    addPercentCell(sheet, row, 4, roi.appreciationPercent);
    addPercentCell(sheet, row, 5, roi.roi);
    addPercentCell(sheet, row, 6, roi.capRate);
    addPercentCell(sheet, row, 7, roi.cashOnCash);
    addPercentCell(sheet, row, 8, roi.irr);
    addDataCell(sheet, row, 9, roi.annualRevenue, true);
    addDataCell(sheet, row, 10, roi.noi, true);
    addPercentCell(sheet, row, 11, roi.noiMargin);
    addPercentCell(sheet, row, 12, roi.marketYield);
    addPercentCell(sheet, row, 13, roi.outperformance);
  });
}

function createTenantExperienceSheet(workbook, monthlyData) {
  const sheet = workbook.addWorksheet("Tenant Experience", {
    properties: { tabColor: { argb: "FFEC4899" } }
  });
  sheet.columns = [
    { width: 15 },
    { width: 12 },
    { width: 18 },
    { width: 12 },
    { width: 12 },
    { width: 15 },
    { width: 15 },
    { width: 18 },
    { width: 15 },
    { width: 15 },
    { width: 15 }
  ];

  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Satisfaction",
      "Change %",
      "Target",
      "Retention %",
      "Renewal %",
      "Response (d)",
      "Requests",
      "Completed",
      "Completion %"
    ],
    "FFEC4899"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const tenant = month.tenantExperience;
    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addDataCell(sheet, row, 3, tenant.overallSatisfaction, true);
    addChangeCell(sheet, row, 4, tenant.changePercent);
    addDataCell(sheet, row, 5, tenant.targetSatisfaction, true);
    addPercentCell(sheet, row, 6, tenant.retentionRate);
    addPercentCell(sheet, row, 7, tenant.renewalRate);
    addDataCell(sheet, row, 8, tenant.avgResponseTime, true);
    addDataCell(sheet, row, 9, tenant.maintenanceRequests, true);
    addDataCell(sheet, row, 10, tenant.completedRequests, true);
    addPercentCell(sheet, row, 11, tenant.completionRate);
  });
}

function createFinancialSheet(workbook, monthlyData) {
  const sheet = workbook.addWorksheet("Financial Performance", {
    properties: { tabColor: { argb: "FF14B8A6" } }
  });
  sheet.columns = [
    { width: 15 },
    { width: 12 },
    { width: 15 },
    { width: 15 },
    { width: 12 },
    { width: 15 },
    { width: 15 },
    { width: 15 },
    { width: 12 },
    { width: 12 }
  ];

  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Base Rent",
      "Service",
      "Parking",
      "Total Rev",
      "Total Cost",
      "NOI (€)",
      "NOI %",
      "Change %"
    ],
    "FF14B8A6"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const fin = month.financial;
    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addDataCell(sheet, row, 3, fin.revenue.baseRent, true);
    addDataCell(sheet, row, 4, fin.revenue.serviceCharges, true);
    addDataCell(sheet, row, 5, fin.revenue.parkingFees, true);
    addDataCell(sheet, row, 6, fin.revenue.total, true);
    addDataCell(sheet, row, 7, fin.costs.total, true);
    addDataCell(sheet, row, 8, fin.noi, true);
    addPercentCell(sheet, row, 9, fin.noiMargin);
    addChangeCell(sheet, row, 10, fin.noiChange);
  });
}
